<script>
  import NavigatedViewer from "bpmn-js/lib/NavigatedViewer.js";
  import "bpmn-js/dist/assets/bpmn-js.css";
  import "bpmn-js/dist/assets/diagram-js.css";
  import "bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css";
  import { onDestroy, onMount, tick } from "svelte";
  import { Loading } from "carbon-components-svelte";
  import { mirrorOrientation } from "lib/util/bpmnOrientation.js";

  export let bpmnXml = "";
  export let loading = false;
  /** Was gerade laeuft - erscheint mittig ueber der Vorschau. */
  export let loadingText = "Generating BPMN Export";
  export let error = "";
  export let orientation = "horizontal";

  let viewdiv;
  let viewer;
  let loadedKey = "";
  let resizeObserver;

  function initViewer() {
    viewer = new NavigatedViewer({
      container: viewdiv,
    });
  }

  async function fitDiagram() {
    if (!viewer) return;
    await tick();
    requestAnimationFrame(() => {
      const canvas = viewer.get("canvas");
      canvas.resized();
      canvas.zoom("fit-viewport");
    });
  }

  async function loadXml(xml, targetOrientation) {
    // Der Schluessel deckt beides ab: neues Diagramm und gewechselte Ausrichtung.
    const key = `${targetOrientation}|${xml}`;
    if (!viewer || !xml || key === loadedKey) return;
    try {
      await viewer.importXML(
        targetOrientation === "vertical" ? mirrorOrientation(xml) : xml,
      );
      loadedKey = key;
      fitDiagram();
    } catch (err) {
      console.error("Failed to render BPMN preview:", err);
    }
  }

  $: if (viewer && bpmnXml) loadXml(bpmnXml, orientation);
  $: if (viewer && bpmnXml && !loading) fitDiagram();

  export async function saveSVG() {
    if (!viewer || !bpmnXml) return "";
    await loadXml(bpmnXml);
    await fitDiagram();
    const result = await viewer.saveSVG();
    return result?.svg || "";
  }

  onMount(() => {
    initViewer();
    resizeObserver = new ResizeObserver(() => {
      fitDiagram();
    });
    if (viewdiv) resizeObserver.observe(viewdiv);
  });

  onDestroy(() => {
    if (resizeObserver) resizeObserver.disconnect();
    if (viewer) viewer.destroy();
  });
</script>

<div class="inline-preview">
  {#if loading}
    <div class="preview_overlay">
      <Loading withOverlay={false} small />
      {#if loadingText}<span class="overlay_text">{loadingText}</span>{/if}
    </div>
  {/if}
  {#if error}
    <div class="preview_message error">{error}</div>
  {:else if !bpmnXml && !loading}
    <div class="preview_message">Live Preview</div>
  {/if}
  <div class:hidden={!bpmnXml} class="preview_canvas" bind:this={viewdiv} />
</div>

<style>
  .inline-preview {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: min(72vh, 52rem);
    overflow: hidden;
    border: 1px solid #d9d9d9;
    border-radius: 0.5rem;
    background-color: var(--pax-white, #fff);
    background-image: radial-gradient(#d6d9de 1px, transparent 1px);
    background-size: 16px 16px;
  }

  .overlay_text {
    color: var(--pax-black, #1d1d1f);
    font-size: 0.86rem;
    font-weight: 600;
    letter-spacing: 0.01em;
  }

  .preview_canvas {
    width: 100%;
    height: 100%;
    min-height: min(72vh, 52rem);
    background-color: transparent;
    background-image: radial-gradient(#d6d9de 1px, transparent 1px);
    background-size: 16px 16px;
  }

  :global(.preview_canvas .djs-container),
  :global(.preview_canvas .djs-container > svg) {
    background-color: transparent !important;
    background-image: radial-gradient(#d6d9de 1px, transparent 1px) !important;
    background-size: 16px 16px !important;
  }

  :global(.inline-preview .bjs-powered-by) {
    display: none !important;
  }

  .hidden {
    visibility: hidden;
  }

  .preview_overlay,
  .preview_message {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.72);
    color: var(--pax-black, #1d1d1f);
    font-size: 1.25rem;
    font-weight: 600;
  }

  .preview_message.error {
    padding: 2rem;
    color: #b42318;
    font-size: 0.9rem;
    text-align: center;
  }
</style>
